import { useState } from 'react';
import PropTypes from 'prop-types';
import { LayoutGrid, Plus, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// ── Static config ──────────────────────────────────────────────────────────

const TEAMS = [
  { id: 'wwfo',       label: 'WWFO: Sales & Renewals',      sub: '' },
  { id: 'channels',   label: 'WWFO: Channels',              sub: '' },
  { id: 'cx',         label: 'WWFO: Services (CS/PS)',       sub: '' },
  { id: 'marketing',  label: 'Marketing',               sub: '' },
  { id: 'people',     label: 'People and Places',       sub: '' },
  { id: 'product',    label: 'Product / Engr',          sub: '' },
  { id: 'legal',      label: 'Legal',                   sub: '' },
  { id: 'finance',    label: 'Finance',                 sub: '' },
  { id: 'it',         label: 'IT / Platform',           sub: '' },
  { id: 'enterprise', label: 'Enterprise',              sub: '' },
];

const TIERS = [
  { id: 1, label: 'Tier 1', sub: 'Enterprise Transformation',                    labelCls: 'bg-[#0F172A] text-white',      rowCls: 'bg-slate-50  dark:bg-slate-900/20' },
  { id: 2, label: 'Tier 2', sub: 'Large-scale Transformation',                   labelCls: 'bg-[#0F172A] text-white',      rowCls: 'bg-white     dark:bg-slate-900/10' },
  { id: 3, label: 'Tier 3', sub: 'Single-function improvement + IT foundations', labelCls: 'bg-[#005C2E] text-white',      rowCls: 'bg-slate-50  dark:bg-slate-900/20' },
  { id: 4, label: 'Tier 4', sub: 'Run and enhance the business',                 labelCls: 'bg-[#00C26D] text-[#1A1A1A]', rowCls: 'bg-white     dark:bg-slate-900/10' },
];

const RAG = {
  red:   { dot: '#D31010', bg: 'rgba(211,16,16,0.25)',  border: '#fca5a5', text: '#B91C1C' },
  amber: { dot: '#F5C400', bg: 'rgba(245,196,0,0.28)',  border: '#fde68a', text: '#8A6800' },
  green: { dot: '#00C26D', bg: 'rgba(0,194,109,0.22)',  border: '#6ee7b7', text: '#00703C' },
};

const PROGRESS_STYLES  = ['Gradient fill', 'Solid fill', 'Outline only', 'No fill'];
const COLUMN_SPANS     = ['1 column', '2 columns', '3 columns', 'Full row'];
const REGULAR_SPANS    = new Set(['1 column', '2 columns', '3 columns']);

let _seq = 1;
const uid = () => `card-${_seq++}`;

const blank = (overrides = {}) => ({
  id: uid(), title: '', tierId: 1, teamIds: ['it'],
  progress: 0, progressStyle: 'Gradient fill',
  fontSize: 11, columnSpan: '1 column', ragStatus: 'green',
  isAi: false,
  ...overrides,
});

// ── Seed data ──────────────────────────────────────────────────────────────

const SEED_DATA = [
  // Tier 1
  blank({ title: 'Optimus - Jazz',          tierId: 1, teamIds: ['wwfo'],       progress: 60, ragStatus: 'green' }),
  blank({ title: 'Daybreak',                tierId: 1, teamIds: ['wwfo'],       progress: 40, ragStatus: 'amber' }),
  blank({ title: 'Daywatch',                tierId: 1, teamIds: ['channels'],   progress: 30, ragStatus: 'green' }),
  blank({ title: 'Rosie - Seller Cockpit',  tierId: 1, teamIds: ['wwfo'],       progress: 55, ragStatus: 'green' }),
  blank({ title: 'FY27 Territory Planning', tierId: 1, teamIds: ['wwfo'],       progress: 20, ragStatus: 'amber' }),
  blank({ title: 'Othello',                 tierId: 1, teamIds: ['it'],         progress: 45, ragStatus: 'green' }),
  blank({ title: 'Cloudpay Replacement',    tierId: 1, teamIds: ['finance'],    progress: 35, ragStatus: 'red'   }),
  blank({ title: 'Token Intent',            tierId: 1, teamIds: ['product'],    progress: 50, ragStatus: 'green' }),
  blank({ title: 'Aura (Axur)',             tierId: 1, teamIds: ['it'],         progress: 25, ragStatus: 'amber' }),
  blank({ title: 'M&A (K-Pop)',             tierId: 1, teamIds: ['enterprise'], progress: 15, ragStatus: 'green' }),
  // Tier 2
  blank({ title: 'MSSP',                        tierId: 2, teamIds: ['wwfo'],      progress: 70, ragStatus: 'green' }),
  blank({ title: 'RADAR',                        tierId: 2, teamIds: ['it'],        progress: 40, ragStatus: 'amber' }),
  blank({ title: 'Whitespace/Goldmine',          tierId: 2, teamIds: ['wwfo'],      progress: 55, ragStatus: 'green' }),
  blank({ title: 'Wayfinder',                    tierId: 2, teamIds: ['cx'],        progress: 30, ragStatus: 'green' }),
  blank({ title: 'Value Cloud Replacement',      tierId: 2, teamIds: ['it'],        progress: 20, ragStatus: 'red'   }),
  blank({ title: 'Infinity',                     tierId: 2, teamIds: ['it'],        progress: 60, ragStatus: 'green' }),
  blank({ title: 'Marketing Naming Opt',         tierId: 2, teamIds: ['marketing'], progress: 80, ragStatus: 'green' }),
  blank({ title: 'Onboarding - Streamlining',    tierId: 2, teamIds: ['cx'],        progress: 45, ragStatus: 'amber' }),
  blank({ title: 'Benefits Rollout',             tierId: 2, teamIds: ['people'],    progress: 65, ragStatus: 'green' }),
  blank({ title: 'Payroll Transformation',       tierId: 2, teamIds: ['finance'],   progress: 35, ragStatus: 'amber' }),
  blank({ title: 'IB on IB',                     tierId: 2, teamIds: ['it'],        progress: 50, ragStatus: 'green' }),
  blank({ title: 'ODA/Identity Normalization',   tierId: 2, teamIds: ['it'],        progress: 25, ragStatus: 'amber' }),
  blank({ title: 'Annual Price Increase',        tierId: 2, teamIds: ['finance'],   progress: 90, ragStatus: 'green' }),
  blank({ title: 'Squeeze the Box',              tierId: 2, teamIds: ['wwfo'],      progress: 40, ragStatus: 'green' }),
  blank({ title: 'Shaktiman',                    tierId: 2, teamIds: ['product'],   progress: 30, ragStatus: 'amber' }),
  blank({ title: 'Hero Agent - Dennis',          tierId: 2, teamIds: ['it'],        progress: 55, ragStatus: 'green', isAi: true }),
  blank({ title: 'AI Playground',                tierId: 2, teamIds: ['it'],        progress: 70, ragStatus: 'green', isAi: true }),
  // Tier 3
  blank({ title: 'Install Base Remediation',         tierId: 3, teamIds: ['it'],        progress: 60, ragStatus: 'amber' }),
  blank({ title: 'Partner LMS',                      tierId: 3, teamIds: ['channels'],  progress: 40, ragStatus: 'green' }),
  blank({ title: 'NIOS -> NIOS X',                   tierId: 3, teamIds: ['product'],   progress: 50, ragStatus: 'green' }),
  blank({ title: 'Data Deletion - MKTO',             tierId: 3, teamIds: ['marketing'], progress: 75, ragStatus: 'green' }),
  blank({ title: 'OTL - Time & Labour Optimization', tierId: 3, teamIds: ['people'],    progress: 30, ragStatus: 'amber' }),
  blank({ title: 'Case Management',                  tierId: 3, teamIds: ['cx'],        progress: 55, ragStatus: 'green' }),
  blank({ title: 'Financial Entity Exp',             tierId: 3, teamIds: ['finance'],   progress: 45, ragStatus: 'green' }),
  blank({ title: 'Anybody Can Develop',              tierId: 3, teamIds: ['it'],        progress: 20, ragStatus: 'green' }),
  blank({ title: 'DL AI-ification',                  tierId: 3, teamIds: ['it'],        progress: 35, ragStatus: 'amber', isAi: true }),
  blank({ title: 'SSL Certificate Automation',       tierId: 3, teamIds: ['it'],        progress: 80, ragStatus: 'green' }),
  blank({ title: 'MAM/BYOD',                         tierId: 3, teamIds: ['it'],        progress: 60, ragStatus: 'green' }),
  blank({ title: 'CW IB Devices',                    tierId: 3, teamIds: ['it'],        progress: 45, ragStatus: 'amber' }),
  // Tier 4
  blank({ title: 'Enhancements',       tierId: 4, teamIds: ['it'], progress: 100, ragStatus: 'green', columnSpan: 'Full row' }),
  blank({ title: 'Keep the Lights On', tierId: 4, teamIds: ['it'], progress: 100, ragStatus: 'green' }),
  blank({ title: 'Tech Debt',          tierId: 4, teamIds: ['it'], progress: 60,  ragStatus: 'amber' }),
];

// ── ProjectCard ────────────────────────────────────────────────────────────

const cardBg = (card, rag) => {
  if (card.progressStyle === 'Gradient fill')
    return { background: `linear-gradient(to right, ${rag.bg} ${card.progress}%, transparent ${card.progress}%)` };
  if (card.progressStyle === 'Solid fill')
    return { background: `linear-gradient(to right, ${rag.dot}44 ${card.progress}%, transparent ${card.progress}%)` };
  return {};
};

const ProjectCard = ({ card, onClick }) => {
  const rag = RAG[card.ragStatus] || RAG.green;
  return (
    <button type="button" onClick={() => onClick(card)}
      className="w-full text-left rounded border px-2 py-1.5 hover:shadow-md transition-all cursor-pointer"
      style={{ borderColor: rag.border, fontSize: `${card.fontSize}px`, ...cardBg(card, rag) }}
    >
      <div className="flex items-start gap-1">
        {card.isAi && (
          <Sparkles className="shrink-0 mt-0.5 text-[#00C26D]" style={{ width: '0.9em', height: '0.9em' }} />
        )}
        <span className="font-semibold leading-snug break-words text-slate-900 dark:text-slate-100">{card.title}</span>
      </div>
    </button>
  );
};
ProjectCard.propTypes = { card: PropTypes.object.isRequired, onClick: PropTypes.func.isRequired };

// ── FullRowSection — cards that span the entire tier width ─────────────────

const FullRowSection = ({ tierId, cards, onCardClick, onAddFullRow }) => {
  const rowCards = cards.filter(c => c.tierId === tierId && c.columnSpan === 'Full row');
  if (rowCards.length === 0) return null;
  return (
    <div className="px-1.5 pt-1.5 pb-1 space-y-1 border-b border-slate-300 dark:border-slate-600 group/fullrow">
      {rowCards.map(card => <ProjectCard key={card.id} card={card} onClick={onCardClick} />)}
      <button type="button" onClick={onAddFullRow}
        className="w-full text-[10px] text-slate-400 hover:text-[#00C26D] hidden group-hover/fullrow:flex items-center justify-center gap-0.5 py-0.5 rounded hover:bg-[#00C26D]/5 transition-colors">
        <Plus className="w-3 h-3" />Add full-row card
      </button>
    </div>
  );
};
FullRowSection.propTypes = {
  tierId: PropTypes.number.isRequired, cards: PropTypes.array.isRequired,
  onCardClick: PropTypes.func.isRequired, onAddFullRow: PropTypes.func.isRequired,
};

// ── BoardCell ──────────────────────────────────────────────────────────────

const BoardCell = ({ tierId, teamId, cards, onCardClick, onAdd }) => {
  const cellCards = cards.filter(c =>
    c.tierId === tierId &&
    Array.isArray(c.teamIds) &&
    c.teamIds.includes(teamId) &&
    REGULAR_SPANS.has(c.columnSpan)
  );
  const minH = tierId === 4 ? 'min-h-[96px]' : 'min-h-[240px]';
  return (
    <div className={`p-1.5 ${minH} border-r border-slate-200 dark:border-slate-700 last:border-r-0 group overflow-hidden w-full`}>
      <div className="space-y-1">
        {cellCards.map(card => <ProjectCard key={card.id} card={card} onClick={onCardClick} />)}
      </div>
      <button type="button" onClick={() => onAdd(tierId, teamId)}
        className="mt-1 w-full text-[10px] text-slate-400 hover:text-[#00C26D] hidden group-hover:flex items-center justify-center gap-0.5 py-0.5 rounded hover:bg-[#00C26D]/5 transition-colors">
        <Plus className="w-3 h-3" />Add
      </button>
    </div>
  );
};
BoardCell.propTypes = {
  tierId: PropTypes.number.isRequired, teamId: PropTypes.string.isRequired,
  cards: PropTypes.array.isRequired, onCardClick: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

// ── EditProjectModal ───────────────────────────────────────────────────────

const EditProjectModal = ({ card, onSave, onDelete, onClose }) => {
  const [draft, setDraft] = useState({ ...card });
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const isNew = !!card._isNew;

  const toggleTeam = (teamId) => {
    const current = draft.teamIds || [];
    set('teamIds', current.includes(teamId)
      ? current.filter(id => id !== teamId)
      : [...current, teamId]);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Project' : 'Edit Card'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Title */}
          <div>
            <label htmlFor="card-title" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Title</label>
            <textarea id="card-title" rows={2} value={draft.title} onChange={e => set('title', e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40 bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
          </div>

          {/* AI Related */}
          <div className="flex items-center justify-between py-1">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#00C26D]" />AI Related
            </span>
            <button type="button" role="switch" aria-checked={draft.isAi}
              onClick={() => set('isAi', !draft.isAi)}
              className="flex items-center gap-2 group">
              <span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 transition-colors duration-200 ${
                draft.isAi ? 'bg-[#00C26D] border-[#00C26D]' : 'bg-slate-200 dark:bg-slate-700 border-transparent'
              }`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  draft.isAi ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 w-16">
                {draft.isAi ? 'AI project' : 'Not AI'}
              </span>
            </button>
          </div>

          {/* Progress */}
          <div>
            <label htmlFor="card-progress" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Progress</label>
            <div className="flex items-center gap-3">
              <input id="card-progress" type="range" min={0} max={100} value={draft.progress}
                onChange={e => set('progress', Number(e.target.value))}
                className="flex-1 accent-[#00C26D]" />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-300 w-10 text-right">{draft.progress}%</span>
            </div>
          </div>

          {/* Style + Font */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="card-progress-style" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Progress Style</label>
              <select id="card-progress-style" value={draft.progressStyle} onChange={e => set('progressStyle', e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40">
                {PROGRESS_STYLES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="card-font-size" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Font Size (px)</label>
              <input id="card-font-size" type="number" min={8} max={20} value={draft.fontSize}
                onChange={e => set('fontSize', Number(e.target.value))}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40" />
            </div>
          </div>

          {/* Tier + Column span */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="card-tier" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Tier</label>
              <select id="card-tier" value={draft.tierId} onChange={e => set('tierId', Number(e.target.value))}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40">
                {TIERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="card-column-span" className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Column Span</label>
              <select id="card-column-span" value={draft.columnSpan} onChange={e => set('columnSpan', e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#00C26D]/40">
                {COLUMN_SPANS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Teams — multi-select */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Team(s) <span className="normal-case font-normal text-slate-400">— select all that apply</span>
            </legend>
            <div className="grid grid-cols-2 gap-1.5">
              {TEAMS.map(t => {
                const checked = (draft.teamIds || []).includes(t.id);
                return (
                  <label key={t.id}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-all select-none ${
                      checked
                        ? 'bg-[#00C26D]/10 border-[#00C26D]/50 text-[#00703C] dark:text-[#00D97E]'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-[#00C26D]/40'
                    }`}>
                    <input type="checkbox" className="accent-[#00C26D] shrink-0"
                      checked={checked}
                      onChange={() => toggleTeam(t.id)} />
                    <span className="truncate">{t.label}</span>
                  </label>
                );
              })}
            </div>
            {/* Select all / none helpers */}
            <div className="flex gap-3 mt-1.5">
              <button type="button" onClick={() => set('teamIds', TEAMS.map(t => t.id))}
                className="text-[10px] text-[#00995A] hover:underline">Select all</button>
              <button type="button" onClick={() => set('teamIds', [])}
                className="text-[10px] text-slate-400 hover:underline">Clear</button>
            </div>
          </fieldset>

          {/* RAG */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">RAG Status</legend>
            <div className="flex gap-2">
              {['red', 'amber', 'green'].map(r => {
                const c = RAG[r];
                const active = draft.ragStatus === r;
                return (
                  <button key={r} type="button" onClick={() => set('ragStatus', r)}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-bold border capitalize transition-all ${active ? 'ring-2 ring-offset-1' : 'opacity-50 hover:opacity-80'}`}
                    style={{ backgroundColor: c.bg, borderColor: c.border, color: c.text, '--tw-ring-color': c.dot }}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Buttons */}
          <div className="flex items-center gap-2 pt-1">
            {!isNew && (
              <button type="button" onClick={() => onDelete(draft.id)}
                className="px-4 py-2 rounded-lg text-sm font-bold border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                Delete
              </button>
            )}
            <div className="flex-1" />
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-bold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={() => onSave(draft)}
              className="px-4 py-2 rounded-lg text-sm font-bold bg-[#00C26D] text-[#1A1A1A] hover:bg-[#00A85D] transition-colors shadow-sm shadow-[#00C26D]/20">
              Save
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
EditProjectModal.propTypes = {
  card: PropTypes.object.isRequired, onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired,
};

// ── PortfolioBoardPage ─────────────────────────────────────────────────────

const PortfolioBoardPage = () => {
  const [cards, setCards] = useState(SEED_DATA);
  const [editing, setEditing] = useState(null);

  const openEdit = (card) => setEditing(card);

  const openAdd = (tierId, teamId) =>
    setEditing(blank({ tierId, teamIds: teamId ? [teamId] : [], _isNew: true }));

  const openAddFullRow = (tierId) =>
    setEditing(blank({ tierId, teamIds: TEAMS.map(t => t.id), columnSpan: 'Full row', _isNew: true }));

  const handleSave = (draft) => {
    const { _isNew, ...card } = draft;
    setCards(prev => prev.some(c => c.id === card.id)
      ? prev.map(c => c.id === card.id ? card : c)
      : [...prev, card]);
    setEditing(null);
  };

  const handleDelete = (id) => {
    setCards(prev => prev.filter(c => c.id !== id));
    setEditing(null);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="portfolio-board">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#00C26D]/10 dark:bg-[#00C26D]/15 rounded-xl flex items-center justify-center">
          <LayoutGrid className="w-6 h-6 text-[#00995A] dark:text-[#00D97E]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Top IT Programs + Projects</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Tiers 1–4 organized by sponsoring function</p>
        </div>
      </div>

      {/* Board */}
      <div className="glass-card rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <div style={{ minWidth: '1540px' }}>

            {/* Column header row */}
            <div className="flex sticky top-0 z-10 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 backdrop-blur">
              <div className="w-[140px] shrink-0" />
              {TEAMS.map(team => (
                <div key={team.id} className="flex-1 min-w-0 p-1.5 border-r border-slate-200 dark:border-slate-700 last:border-r-0">
                  <div className="bg-[#00C26D] rounded-lg px-2 py-2 text-center overflow-hidden">
                    <p className="text-xs font-bold text-white leading-tight break-words">{team.label}</p>
                    {team.sub && <p className="text-[9px] text-white/75 leading-tight mt-0.5 break-words">{team.sub}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Tier rows */}
            {TIERS.map(tier => (
              <div key={tier.id} className={`flex border-b border-slate-200 dark:border-slate-700 last:border-b-0 ${tier.rowCls}`}>
                {/* Tier label */}
                <div className={`w-[140px] shrink-0 flex flex-col items-center justify-center px-3 py-4 border-r border-slate-200 dark:border-slate-700 ${tier.labelCls}`}>
                  <span className="text-sm font-extrabold tracking-wide">{tier.label}</span>
                  <span className="text-[9px] font-medium mt-1.5 text-center leading-snug opacity-75">{tier.sub}</span>
                </div>

                {/* Right section: full-row cards + team grid */}
                <div className="flex-1 min-w-0 flex flex-col">
                  {/* Full-row cards */}
                  <FullRowSection
                    tierId={tier.id}
                    cards={cards}
                    onCardClick={openEdit}
                    onAddFullRow={() => openAddFullRow(tier.id)}
                  />
                  {/* Per-team cells */}
                  <div className="flex flex-1">
                    {TEAMS.map(team => (
                      <div key={team.id} className="flex-1 min-w-0 overflow-hidden">
                        <BoardCell
                          tierId={tier.id} teamId={team.id}
                          cards={cards} onCardClick={openEdit} onAdd={openAdd}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>

      {/* Edit / Add modal */}
      {editing && (
        <EditProjectModal
          card={editing}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default PortfolioBoardPage;
